/**
 * Las RESPUESTAS de un formulario web como columnas: qué tipo de dato es cada campo, cuáles se enseñan de inicio,
 * cómo se filtran, se ordenan y se grafican. Genérico A PROPÓSITO: no sabe nada de Safevity ni de la conferencia; lo
 * deduce de las respuestas que hay (`web_leads.payload`), así un formulario nuevo de cualquier sitio sale bien sin
 * tocar código. Puro (sin React): lo usan la tabla y la pestaña Analítica de `pages/FormulariosWeb.tsx`.
 *
 * Cómo se decide el tipo de un campo (sobre los valores NO vacíos de todas las respuestas cargadas):
 *   multiple    alguna respuesta es una lista (casillas del formulario)
 *   si_no       todo es verdadero/falso o sí/no
 *   correo      todo parece correo
 *   url         todo empieza con http(s):// (se enseña sólo la ruta)
 *   telefono    el nombre del campo dice teléfono/celular/WhatsApp, o el valor trae 10–15 dígitos con +, espacios o guiones
 *   numero      todo es número (también "$1,500" o "12.5"); un número de 8+ dígitos sin decimales es un id, no un número
 *   fecha       todo es AAAA-MM-DD (con o sin hora) o d/m/aaaa
 *   texto_largo en promedio más de 60 letras (o alguna de más de 140): comentarios. Nunca es columna de inicio.
 *   opcion      pocos valores distintos (≤ 12) que se repiten (o pocas respuestas): se filtra con casillas
 *   texto       lo demás: se filtra con "contiene"
 * Un campo con UN solo valor en todas las respuestas que lo traen es `constante` (no aporta como columna: "documento"
 * de la conferencia dice lo mismo en las 117).
 */

export type TipoCampo = 'numero' | 'fecha' | 'si_no' | 'opcion' | 'multiple' | 'correo' | 'telefono' | 'url' | 'texto' | 'texto_largo';

export const TIPO_TXT: Record<TipoCampo, string> = {
  numero: 'número', fecha: 'fecha', si_no: 'sí / no', opcion: 'opción', multiple: 'varias opciones', correo: 'correo',
  telefono: 'teléfono', url: 'página', texto: 'texto', texto_largo: 'texto largo',
};

export interface ValorConteo { valor: string; n: number }
export interface Campo {
  clave: string;
  etiqueta: string;
  tipo: TipoCampo;
  /** Respuestas (registros) que traen este campo con algo. */
  respondidas: number;
  /** Registros considerados. */
  total: number;
  distintos: number;
  constante: boolean;
  /** Valores con su conteo (normalizados como se enseñan), del más frecuente al menos. Sólo los primeros 60. */
  valores: ValorConteo[];
  min?: number; max?: number;
}

/** Los campos que no son respuestas: el sitio los manda para saber de dónde vino el registro (y los ids técnicos de la
 *  atribución y del reenvío). Tampoco lo que empieza con "_" (página, liga de llegada, ids de Halory…), salvo `_pagina`,
 *  que la conferencia ya enseñaba como columna. */
export const CAMPOS_SISTEMA = new Set(['formulario', 'envio_id', 'event_id', 'fbc', 'fbp', 'fbclid', 'gclid', 'anonimo']);
export const esCampoSistema = (k: string) => CAMPOS_SISTEMA.has(k) || (k.startsWith('_') && k !== '_pagina');

const LABEL: Record<string, string> = {
  capital: 'Capital', interes: 'Interés', formulario: 'Formulario', documento: 'Documento', pais: 'País', origen: 'Llegó por',
  _pagina: 'Página', comentarios: 'Comentarios', edad: 'Edad', zona: 'Zona de interés', puntaje: 'Puntaje', nivel: 'Nivel',
  utm_source: 'Fuente (utm)', utm_medium: 'Medio (utm)', utm_campaign: 'Campaña (utm)', utm_content: 'Anuncio (utm)', utm_term: 'Término (utm)',
  ad_id: 'Anuncio de Meta',
};
/** `form_submission[custom_4]` → "Custom 4"; `tiene_local` → "Tiene local". */
export function etiquetaCampo(clave: string): string {
  if (LABEL[clave]) return LABEL[clave];
  const m = /^[a-z_]+\[([^\]]+)\]$/i.exec(clave);
  const base = (m ? m[1] : clave).replace(/^_+/, '').replace(/[_-]+/g, ' ').trim();
  if (LABEL[base]) return LABEL[base];
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : clave;
}

const vacio = (v: unknown) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
const sinAcentos = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const SI = new Set(['si', 'sí', 'true', 'yes', 'verdadero']);
const NO = new Set(['no', 'false', 'falso']);
const RE_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RE_URL = /^https?:\/\//i;
const RE_NUM = /^[-+]?\$?\s*(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;
const RE_FECHA_ISO = /^(\d{4})-(\d{2})-(\d{2})([T ][\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;
const RE_FECHA_MX = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const PISTA_TEL = /(tel|phone|cel|whats|movil|móvil)/i;

function esSiNo(v: unknown): boolean {
  if (typeof v === 'boolean') return true;
  return typeof v === 'string' && (SI.has(sinAcentos(v)) || NO.has(sinAcentos(v)));
}
/** true / false de un valor sí/no; null si no lo es. */
export function siNo(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') { const t = sinAcentos(v); if (SI.has(t)) return true; if (NO.has(t)) return false; }
  return null;
}
/** Número de un valor ("$1,500.50" → 1500.5); null si no es número. */
export function numero(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (!RE_NUM.test(t)) return null;
  const n = Number(t.replace(/[$,\s+]/g, ''));
  return Number.isFinite(n) ? n : null;
}
const digitos = (s: string) => s.replace(/\D/g, '').length;
/** 'AAAA-MM-DD' de un valor de fecha; null si no lo es (o si no es una fecha que exista). */
export function fechaDia(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  let a: number, m: number, d: number;
  const iso = RE_FECHA_ISO.exec(t);
  if (iso) { a = +iso[1]; m = +iso[2]; d = +iso[3]; if (iso[4]) { const x = new Date(t); if (!Number.isNaN(x.getTime())) return x.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }); } }
  else { const mx = RE_FECHA_MX.exec(t); if (!mx) return null; d = +mx[1]; m = +mx[2]; a = +mx[3]; }
  const f = new Date(Date.UTC(a, m - 1, d));
  if (f.getUTCFullYear() !== a || f.getUTCMonth() !== m - 1 || f.getUTCDate() !== d) return null;
  return `${a}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function esTelefono(clave: string, v: unknown): boolean {
  const t = typeof v === 'number' ? String(v) : typeof v === 'string' ? v.trim() : '';
  if (!t || !/^[+()\d\s.-]+$/.test(t)) return false;
  const n = digitos(t);
  if (PISTA_TEL.test(clave)) return n >= 7 && n <= 15;
  return n >= 10 && n <= 15 && /[+\s()-]/.test(t);
}

/** El texto con el que se enseña (y se agrupa) un valor, ya con su tipo. */
export function textoValor(clave: string, tipo: TipoCampo, v: unknown): string {
  if (vacio(v)) return '';
  if (Array.isArray(v)) return v.map((x) => textoValor(clave, 'texto', x)).filter(Boolean).join(', ');
  if (tipo === 'si_no') { const b = siNo(v); return b === null ? String(v) : b ? 'Sí' : 'No'; }
  if (tipo === 'numero') { const n = numero(v); return n === null ? String(v) : n.toLocaleString('es-MX', { maximumFractionDigits: 2 }); }
  if (tipo === 'fecha') {
    const f = fechaDia(v); if (!f) return String(v);
    const [a, m, d] = f.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d)).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  if (tipo === 'url') { try { const u = new URL(String(v)); return (u.pathname + u.search) || u.host; } catch { return String(v); } }
  if (clave === 'origen') return ({ qr: 'QR de la presentación', directo: 'liga directa', prueba: 'prueba' } as Record<string, string>)[String(v)] || String(v);
  if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return String(v); } }
  return String(v).trim();
}
/** Los elementos de una respuesta de varias opciones (o el valor solo). */
export function elementos(clave: string, v: unknown): string[] {
  if (vacio(v)) return [];
  if (Array.isArray(v)) return v.map((x) => textoValor(clave, 'texto', x)).filter(Boolean);
  return [textoValor(clave, 'texto', v)].filter(Boolean);
}

export function inferirTipo(clave: string, valores: unknown[]): TipoCampo {
  const vs = valores.filter((v) => !vacio(v));
  if (!vs.length) return 'texto';
  if (vs.some(Array.isArray)) return 'multiple';
  if (vs.every(esSiNo)) return 'si_no';
  const strs = vs.map((v) => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : ''));
  if (vs.every((v) => typeof v === 'string' && RE_CORREO.test(v.trim()))) return 'correo';
  if (vs.every((v) => typeof v === 'string' && RE_URL.test(v.trim()))) return 'url';
  if (vs.every((v) => esTelefono(clave, v))) return 'telefono';
  // Un identificador (ad_id, campaign_id…) es texto aunque sean puros dígitos: "120,001" no es una cantidad.
  const esId = /(^|_)id$/i.test(clave);
  if (!esId && vs.every((v) => numero(v) !== null) && !vs.some((v) => typeof v === 'string' && !/[.,$]/.test(v) && digitos(v) >= 8)) return 'numero';
  if (vs.every((v) => fechaDia(v) !== null)) return 'fecha';
  if (vs.some((v) => typeof v === 'object')) return 'texto';
  const largos = strs.map((s) => s.length);
  const prom = largos.reduce((a, b) => a + b, 0) / largos.length;
  if (prom > 60 || Math.max(...largos) > 140) return 'texto_largo';
  const distintos = new Set(strs.map(sinAcentos)).size;
  if (distintos <= 12 && Math.max(...largos) <= 80 && (distintos <= vs.length / 2 || vs.length < 8)) return 'opcion';
  return 'texto';
}

/** Los campos de estas respuestas, en el orden en que aparecen. */
export function inferirCampos(registros: { campos: Record<string, unknown> }[]): Campo[] {
  const orden: string[] = []; const porClave = new Map<string, unknown[]>();
  for (const r of registros) {
    for (const [k, v] of Object.entries(r.campos || {})) {
      if (esCampoSistema(k)) continue;
      if (!porClave.has(k)) { porClave.set(k, []); orden.push(k); }
      if (!vacio(v)) porClave.get(k)!.push(v);
    }
  }
  return orden.map((clave) => {
    const vs = porClave.get(clave)!;
    const tipo = inferirTipo(clave, vs);
    const cuenta = new Map<string, number>();
    for (const v of vs) for (const t of (tipo === 'multiple' ? elementos(clave, v) : [textoValor(clave, tipo, v)])) if (t) cuenta.set(t, (cuenta.get(t) || 0) + 1);
    const valores = [...cuenta.entries()].map(([valor, n]) => ({ valor, n })).sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor, 'es'));
    const c: Campo = { clave, etiqueta: etiquetaCampo(clave), tipo, respondidas: vs.length, total: registros.length, distintos: cuenta.size,
      constante: cuenta.size === 1 && vs.length > 1, valores: valores.slice(0, 60) };
    if (tipo === 'numero') { const ns = vs.map(numero).filter((n): n is number => n !== null); c.min = Math.min(...ns); c.max = Math.max(...ns); }
    return c;
  });
}

/** Qué tan útil es un campo como columna de inicio (0 = nunca). */
function puntaje(c: Campo): number {
  if (c.constante || c.respondidas === 0) return 0;
  const base: Record<TipoCampo, number> = { opcion: 5, si_no: 5, numero: 4.5, fecha: 4, multiple: 3.5, url: 2, texto: 2, telefono: 1, correo: 1, texto_largo: 0 };
  let p = base[c.tipo];
  if (c.tipo === 'url' && c.distintos > 12) p = 0.5;
  if (c.tipo === 'texto' && c.distintos > 30) p = 1;
  return p * (0.4 + 0.6 * (c.respondidas / Math.max(1, c.total)));
}
/** Las columnas con las que abre la tabla (hasta `max`, en el orden del formulario). */
export function columnasDeInicio(campos: Campo[], max = 5): string[] {
  const elegidas = new Set(campos.map((c) => ({ c, p: puntaje(c) })).filter((x) => x.p > 0).sort((a, b) => b.p - a.p).slice(0, max).map((x) => x.c.clave));
  return campos.filter((c) => elegidas.has(c.clave)).map((c) => c.clave);
}

// ------------------------------------------------------------------ filtros
export type Filtro =
  | { tipo: 'valores'; valores: string[] }          // opcion / si_no / multiple / url: cualquiera de estos
  | { tipo: 'rango'; desde: string; hasta: string }  // numero (como texto del campo) / fecha (AAAA-MM-DD)
  | { tipo: 'contiene'; texto: string }              // texto, correo, teléfono, texto largo
  | { tipo: 'respondio'; si: boolean };              // contestó o no el campo (cualquier tipo)
export type Filtros = Record<string, Filtro>;

/** El tipo de filtro que le toca a un campo. */
export function filtroPara(t: TipoCampo): Filtro['tipo'] {
  if (t === 'opcion' || t === 'si_no' || t === 'multiple' || t === 'url') return 'valores';
  if (t === 'numero' || t === 'fecha') return 'rango';
  return 'contiene';
}
export function filtroActivo(f: Filtro | undefined): boolean {
  if (!f) return false;
  if (f.tipo === 'valores') return f.valores.length > 0;
  if (f.tipo === 'rango') return f.desde !== '' || f.hasta !== '';
  if (f.tipo === 'contiene') return f.texto.trim() !== '';
  return true;
}
export function cumple(c: Campo, v: unknown, f: Filtro): boolean {
  if (f.tipo === 'respondio') return f.si ? !vacio(v) : vacio(v);
  if (vacio(v)) return false;
  if (f.tipo === 'valores') {
    const es = c.tipo === 'multiple' ? elementos(c.clave, v) : [textoValor(c.clave, c.tipo, v)];
    return es.some((x) => f.valores.includes(x));
  }
  if (f.tipo === 'rango') {
    if (c.tipo === 'fecha') { const d = fechaDia(v); if (!d) return false; return (!f.desde || d >= f.desde) && (!f.hasta || d <= f.hasta); }
    const n = numero(v); if (n === null) return false;
    const a = f.desde === '' ? null : Number(f.desde), b = f.hasta === '' ? null : Number(f.hasta);
    return (a === null || Number.isNaN(a) || n >= a) && (b === null || Number.isNaN(b) || n <= b);
  }
  const t = sinAcentos(f.texto);
  return sinAcentos(textoValor(c.clave, c.tipo, v)).includes(t) || sinAcentos(String(Array.isArray(v) ? v.join(' ') : v)).includes(t);
}
/** El filtro en palabras, para la pastilla que lo quita ("País: MX, ES", "Edad: 30–45"). */
export function filtroTexto(c: Campo, f: Filtro): string {
  if (f.tipo === 'valores') return `${c.etiqueta}: ${f.valores.join(', ')}`;
  if (f.tipo === 'contiene') return `${c.etiqueta} contiene «${f.texto.trim()}»`;
  if (f.tipo === 'respondio') return `${c.etiqueta}: ${f.si ? 'contestó' : 'sin contestar'}`;
  const fmt = (x: string) => (c.tipo === 'fecha' ? textoValor(c.clave, 'fecha', x) : Number(x).toLocaleString('es-MX'));
  if (f.desde && f.hasta) return `${c.etiqueta}: ${fmt(f.desde)} – ${fmt(f.hasta)}`;
  return f.desde ? `${c.etiqueta}: desde ${fmt(f.desde)}` : `${c.etiqueta}: hasta ${fmt(f.hasta)}`;
}

// ------------------------------------------------------------------ orden
/** Compara dos valores de un campo con su tipo; lo vacío va siempre al final (en los dos sentidos). */
export function comparar(c: Campo, a: unknown, b: unknown, dir: 1 | -1): number {
  const va = vacio(a), vb = vacio(b);
  if (va || vb) return va && vb ? 0 : va ? 1 : -1;
  let r = 0;
  if (c.tipo === 'numero') r = (numero(a) ?? 0) - (numero(b) ?? 0);
  else if (c.tipo === 'fecha') r = (fechaDia(a) || '').localeCompare(fechaDia(b) || '');
  else if (c.tipo === 'si_no') r = Number(siNo(a)) - Number(siNo(b));
  else r = textoValor(c.clave, c.tipo, a).localeCompare(textoValor(c.clave, c.tipo, b), 'es', { sensitivity: 'base', numeric: true });
  return r * dir;
}

// ------------------------------------------------------------------ analítica
export function estadisticas(ns: number[]): { n: number; media: number; mediana: number; min: number; max: number } | null {
  if (!ns.length) return null;
  const s = [...ns].sort((a, b) => a - b);
  const mitad = Math.floor(s.length / 2);
  return { n: s.length, media: s.reduce((a, b) => a + b, 0) / s.length, mediana: s.length % 2 ? s[mitad] : (s[mitad - 1] + s[mitad]) / 2, min: s[0], max: s[s.length - 1] };
}
/** Histograma con cortes "redondos" (1, 2, 5 × 10^k), entre 4 y 10 barras. */
export function histograma(ns: number[]): { desde: number; hasta: number; n: number; etiqueta: string }[] {
  if (!ns.length) return [];
  const min = Math.min(...ns), max = Math.max(...ns);
  if (min === max) return [{ desde: min, hasta: max, n: ns.length, etiqueta: min.toLocaleString('es-MX') }];
  const crudo = (max - min) / 8;
  const p = Math.pow(10, Math.floor(Math.log10(crudo)));
  const paso = [1, 2, 2.5, 5, 10].map((m) => m * p).find((x) => x >= crudo) || crudo;
  const ini = Math.floor(min / paso) * paso;
  const cajas: { desde: number; hasta: number; n: number; etiqueta: string }[] = [];
  for (let x = ini; x <= max; x += paso) cajas.push({ desde: x, hasta: x + paso, n: 0, etiqueta: '' });
  for (const n of ns) { const i = Math.min(cajas.length - 1, Math.floor((n - ini) / paso)); cajas[i].n++; }
  const corto = (x: number) => (Math.abs(x) >= 1e6 ? `${+(x / 1e6).toFixed(1)} M` : Math.abs(x) >= 1e4 ? `${+(x / 1e3).toFixed(0)} mil` : x.toLocaleString('es-MX'));
  for (const c of cajas) c.etiqueta = `${corto(c.desde)}–${corto(c.hasta)}`;
  return cajas;
}
const VACIAS = new Set(('de la que el en y a los del se las por un para con no una su al lo como mas más pero sus le ya o este sí si porque esta entre cuando muy sin sobre tambien también me hasta hay donde quien desde todo nos durante todos uno les ni contra otros ese eso ante ellos e esto mí antes algunos qué unos yo otro otras otra él tanto esa estos mucho quienes nada muchos cual poco ella estar estas algunas algo nosotros mi mis tú te ti tu tus ellas nosotras vosotros vosotras os mío mía míos mías tuyo tuya suyo suya nuestro nuestra vuestro vuestra es son fue ser tengo tiene tener quiero quisiera saber the and of to in').split(' '));
/** Las palabras que se repiten en un texto libre (en cuántas respuestas sale cada una; sin las vacías: "de", "que"…).
 *  Se agrupan sin acentos ni mayúsculas (la ñ se respeta: "año" no es "ano") y se enseñan como se escribieron más. */
export function palabras(textos: string[], max = 12): ValorConteo[] {
  const c = new Map<string, number>(); const formas = new Map<string, Map<string, number>>();
  const llave = (w: string) => w.replace(/ñ/g, '\u0001').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\u0001/g, 'ñ');
  for (const t of textos) {
    const vistas = new Set<string>();
    for (const w of t.toLowerCase().split(/[^a-záéíóúüñ0-9]+/)) {
      const k = llave(w);
      if (k.length < 3 || VACIAS.has(k) || VACIAS.has(w) || /^\d+$/.test(k)) continue;
      if (!formas.has(k)) formas.set(k, new Map());
      formas.get(k)!.set(w, (formas.get(k)!.get(w) || 0) + 1);
      vistas.add(k);
    }
    for (const k of vistas) c.set(k, (c.get(k) || 0) + 1);
  }
  const forma = (k: string) => [...formas.get(k)!.entries()].sort((a, b) => b[1] - a[1])[0][0];
  return [...c.entries()].filter(([, n]) => n > 1).map(([k, n]) => ({ valor: forma(k), n })).sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor, 'es')).slice(0, max);
}
